<template>
    <div class="reviews_page">

            <div class="period_tabs mb-3">
                <button
                    v-for="p in period_options"
                    :key="p.key"
                    class="period_tab_btn"
                    :class="{active: period === p.key}"
                    @click="set_period(p.key)"
                >
                    {{ __t(p.label_key) }}
                </button>
            </div>

            <div class="row mb-3">
                <div class="col-md-6 col-lg-4 mb-2">
                    <div class="reviews_select_wrap">
                        <i class="fal fa-store reviews_select_icon"></i>
                        <v-select
                            v-model="filter.rest_id"
                            class="reviews_select"
                            label="title"
                            :reduce="rest => rest.id"
                            :options="restaurants"
                            :placeholder="__t('ALL_RESTAURANTS')"
                            @input="reload"
                        >
                            <template slot="no-options">{{ __t('ALL_RESTAURANTS') }}</template>

                            <template slot="option" slot-scope="option">
                                <div class="rest_opt_title">{{ option.title }}</div>
                                <div class="rest_opt_address" v-if="option.address">{{ option.address }}</div>
                            </template>

                            <template slot="selected-option" slot-scope="option">
                                {{ option.title }}
                            </template>
                        </v-select>
                    </div>
                </div>

                <div class="col-md-6 col-lg-4 mb-2">
                    <div class="reviews_select_wrap">
                        <i class="fal fa-comment-alt-lines reviews_select_icon"></i>
                        <v-select
                            v-model="filter.source"
                            class="reviews_select"
                            label="human_name"
                            :reduce="src => src.type"
                            :options="sources"
                            :placeholder="__t('ALL_REVIEW_SOURCES')"
                            @input="reload"
                        >
                            <template slot="no-options">{{ __t('ALL_REVIEW_SOURCES') }}</template>

                            <template slot="option" slot-scope="option">
                                <span class="source_opt_icon" v-html="option.icon"></span>
                                {{ option.human_name }}
                            </template>

                            <template slot="selected-option" slot-scope="option">
                                <span class="source_opt_icon" v-html="option.icon"></span>
                                {{ option.human_name }}
                            </template>
                        </v-select>
                    </div>
                </div>
            </div>

            <div class="stat_grid mb-4">
                <div class="stat_card">
                    <div class="stat_card__icon bg-warning-50">
                        <i class="fal fa-star color-warning-500"></i>
                    </div>
                    <div class="stat_card__body">
                        <span class="stat_card__label">{{ __t('AVG_RATIO') }}</span>
                        <span class="stat_card__value">{{ stata.avg_ratio || 0 }}</span>
                    </div>
                </div>

                <div class="stat_card">
                    <div class="stat_card__icon bg-primary-50">
                        <i class="fal fa-comment-alt-lines color-primary-500"></i>
                    </div>
                    <div class="stat_card__body">
                        <span class="stat_card__label">{{ __t('REVIEWS_COUNT') }}</span>
                        <span class="stat_card__value">{{ stata.count || 0 }}</span>
                    </div>
                </div>

                <div class="stat_card">
                    <div class="stat_card__icon bg-success-50">
                        <i class="fal fa-thumbs-up color-success-500"></i>
                    </div>
                    <div class="stat_card__body">
                        <span class="stat_card__label">{{ __t('POSITIVE_REVIEWS') }}</span>
                        <span class="stat_card__value color-success-500">
                            {{ stata.positive.count || 0 }}
                            <small class="opacity-50" v-if="stata.positive.percent">{{ stata.positive.percent }}%</small>
                        </span>
                    </div>
                </div>

                <div class="stat_card">
                    <div class="stat_card__icon bg-danger-50">
                        <i class="fal fa-thumbs-down color-danger-500"></i>
                    </div>
                    <div class="stat_card__body">
                        <span class="stat_card__label">{{ __t('NEGATIVE_REVIEWS') }}</span>
                        <span class="stat_card__value color-danger-500">
                            {{ stata.negative.count || 0 }}
                            <small class="opacity-50" v-if="stata.negative.percent">{{ stata.negative.percent }}%</small>
                        </span>
                    </div>
                </div>
            </div>

            <div id="panel-4" class="panel">
                <div class="panel-container show">
                    <div class="panel-content">

                        <b-skeleton-table
                            v-if="is_load"
                            :rows="this.cur_records"
                            :columns="2"
                            :hide-header="true"
                            :table-props="{ bordered: false, striped: true }"
                        >
                        </b-skeleton-table>

                        <div class="inbox_vue_list" v-if="!is_load">

                            <div class="inb_pagi pt-2" v-if="rows.length > 0">
                                <pagination_list
                                    key="up"
                                    :total="pagination.total"
                                    :limit="pagination.limit"
                                    :current="pagination.pagesCurrent"
                                    @page-change="handlePageChange">
                                </pagination_list>
                            </div>

                            <b-table
                                v-if="rows.length > 0"
                                :tbody-tr-class="rowClass"
                                class="table-responsive-xl resolution_table reviews_table" :items="rows" :fields="fields">

                                <template v-slot:head(customer_name)="data">
                                    <grid_list
                                        :list_ordering="'customer_name'"
                                        :field_name="__t('CUSTOMER')"
                                        :current_ordering="list.ordering"
                                        :current_direction="list.direction"
                                        @update-order="handleOrdering"
                                    />
                                </template>

                                <template v-slot:head(ratio)="data">
                                    <grid_list
                                        :list_ordering="'ratio'"
                                        :field_name="__t('RATIO')"
                                        :current_ordering="list.ordering"
                                        :current_direction="list.direction"
                                        @update-order="handleOrdering"
                                    />
                                </template>

                                <template v-slot:head(added)="data">
                                    <grid_list
                                        :list_ordering="'added'"
                                        :field_name="__t('added')"
                                        :current_ordering="list.ordering"
                                        :current_direction="list.direction"
                                        @update-order="handleOrdering"
                                    />
                                </template>

                                <template v-slot:head(description)="data">
                                    {{ __t('REVIEW_TEXT') }}
                                </template>

                                <template v-slot:head(source)="data">
                                    {{ __t('PROVIDER') }}
                                </template>

                                <template v-slot:head(location)="data">
                                    {{ __t('LOCATION') }}
                                </template>

                                <!-- body -->
                                <template v-slot:cell(customer_name)="data">
                                    <div class="d-flex align-items-center w-100 cursor-pointer" v-on:click="open_item(data.item)">
                                        <div class="review_avatar mr-2">
                                            {{ customer_initial(data.item.customer_name) }}
                                        </div>
                                        <div class="min-w-0">
                                            <div class="text-truncate">{{ data.item.customer_name }}</div>
                                            <div class="small text-muted" v-if="data.item.customer_phone">
                                                {{ data.item.customer_phone }}
                                            </div>
                                        </div>
                                    </div>
                                </template>

                                <template v-slot:cell(ratio)="data">
                                    <div class="rating-stars d-flex align-items-center">
                                        <i
                                            v-for="n in 5"
                                            :key="n"
                                            class="fa-star"
                                            :class="n <= data.item.ratio ? 'fas text-warning' : 'far text-muted'"
                                        ></i>
                                        <span class="small text-muted ml-2">{{ data.item.ratio }}.0</span>
                                    </div>
                                </template>

                                <template v-slot:cell(added)="data">
                                    <div>{{ added_date(data.item.added) }}</div>
                                    <div class="small text-muted">{{ added_time(data.item.added) }}</div>
                                </template>

                                <template v-slot:cell(description)="data">
                                    <div class="cursor-pointer" v-on:click="open_item(data.item)">
                                        <span class="badge rounded-pill bg-primary-50 color-primary-500 mr-2" v-if="data.item.status">
                                            {{ __t('NEW') }}
                                        </span>
                                        <span class="text-truncate-2" v-if="data.item.description">{{ data.item.description }}</span>
                                        <span class="text-muted opacity-50" v-else>—</span>
                                    </div>
                                </template>

                                <template v-slot:cell(source)="data">
                                    <span class="badge rounded-pill source_badge" v-if="obj_to_null(data.item.source)">
                                        {{ data.item.source.human_name }}
                                    </span>
                                </template>

                                <template v-slot:cell(location)="data">
                                    <div class="text-muted" v-if="obj_to_null(data.item.location)">
                                        {{ data.item.location.rest ? data.item.location.rest.title : data.item.location.title }}
                                    </div>
                                </template>
                            </b-table>

                            <div class="text-center opacity-50 p-4" v-if="rows.length === 0">
                                {{ __t('NO_DATA') }}
                            </div>

                            <div class="inb_pagi pt-2" v-if="rows.length > 0">
                                <pagination_list
                                    key="down"
                                    :total="pagination.total"
                                    :limit="pagination.limit"
                                    :current="pagination.pagesCurrent"
                                    @page-change="handlePageChange">
                                </pagination_list>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

        <catalog_review_modal></catalog_review_modal>
        <review_location_modal></review_location_modal>

    </div>
</template>

<script>

import {mapGetters} from "vuex";
import vSelect from 'vue-select';

import Grid_list from "../../sys/list/grid_list.vue";
import Pagination_list from "../../sys/list/pagination_list.vue";
import Catalog_review_modal from "./item/catalog_review_modal.vue";
import Review_location_modal from "../../review/location/review_location_modal.vue";

export default {
    name: "catalog_reviews",
    components : {
        Catalog_review_modal,
        Review_location_modal,
        Grid_list,
        Pagination_list,
        vSelect,
    },
    data() {
        return {
            period: 'year',

            rows: [],
            is_load: true,
            cur_records: 8,

            restaurants: [],
            sources: [],

            stata: {
                avg_ratio: 0,
                count: 0,
                positive: {count: 0, percent: 0},
                negative: {count: 0, percent: 0},
            },

            list: {
                ordering: "added",
                direction: "DESC",
                start: 0,
                limit: 30
            },

            pagination: {
                total: 0,
                limit: 30,
                pagesCurrent: 1
            },

            filter: {
                search: "",
                rest_id: null,
                source: null,
                period: 'year',
            },

            period_options: [
                {key: 'today', label_key: 'TODAY'},
                {key: 'week', label_key: 'LAST_7_DAYS'},
                {key: 'month', label_key: 'THIS_MONTH'},
                {key: 'year', label_key: 'THIS_YEAR'},
            ],

            fields: [
                {key: 'customer_name', label: '', class: ''},
                {key: 'ratio', label: '', class: ''},
                {key: 'added', label: '', class: ''},
                {key: 'description', label: '', class: ''},
                {key: 'source', label: '', class: ''},
                {key: 'location', label: '', class: ''},
            ]
        }
    },

    mounted() {
        this.load_restaurants();

        if (window.hasOwnProperty("review_api")) {
            this.set_period('year');
            this.load_sources();
        }
    },

    created() {
        EventBus.$on('loaded_api', this.on_api_ready);
    },

    methods : {

        on_api_ready() {
            this.set_period(this.period);
            this.load_sources();
        },

        set_period(key) {
            this.period = key;
            this.filter.period = key;
            this.reload();
        },

        reload() {
            this.list.start = 0;
            this.pagination.pagesCurrent = 1;
            this.load_items();
            this.load_stata();
        },

        handleOrdering({ordering, direction}) {
            this.list.ordering = ordering;
            this.list.direction = direction;
            this.load_items();
        },

        handlePageChange({page, start}) {
            this.list.start = start;
            this.load_items();
        },

        async load_restaurants() {
            this.restaurants = [];
            await api.post('/internal/v1/catalog/restaurants', {
                filter: {},
                list: {limit: 200}
            }).then(({data}) => {
                if (data.status === "operation_success") {
                    this.restaurants = data.data;
                }
            }).catch(() => {
            });
        },

        async load_sources() {
            this.sources = [];
            await review_api.get('/reviews/sources/list').then(({data}) => {
                if (data.status === "operation_success") {
                    this.sources = data.data;
                }
            }).catch(() => {
            });
        },

        async load_items() {
            let self = this;
            this.rows = [];
            this.is_load = true;

            await review_api.post('/reviews', {
                filter: this.filter,
                list: this.list
            })
                .then(({data}) => {
                    self.is_load = false;
                    if (data.status === "operation_success") {
                        self.rows = data.data;

                        if (data.message?.pagination) {
                            self.pagination = data.message.pagination;
                        }
                    }
                })
                .catch(({response}) => {
                    self.is_load = false;
                    this.rows = [];
                    EventBus.$emit('show_toast', {
                        err: response.data.message,
                        type: "danger",
                        title: response.data.status
                    });
                });
        },

        async load_stata() {
            await review_api.post('/reviews/stata', {
                filter: this.filter,
                list: this.list
            }).then(({data}) => {
                if (data.status === "operation_success") {
                    this.stata = {
                        avg_ratio: data.data.avg_ratio || 0,
                        count: data.data.count || 0,
                        positive: data.data.positive || {count: 0, percent: 0},
                        negative: data.data.negative || {count: 0, percent: 0},
                    };
                }
            }).catch(() => {
            });
        },

        rowClass(item) {
            if (!item) return;
            return item.class;
        },

        open_item(item) {
            if (item.status) {
                review_api.patch('/reviews/' + item.id).catch(() => {
                });
                item.status = false;
            }
            EventBus.$emit("review_show", parseInt(item.id));
        },

        customer_initial(name) {
            return name ? name.trim().charAt(0).toUpperCase() : '?';
        },

        added_date(added) {
            return added ? added.split(' ')[0] : '';
        },

        added_time(added) {
            return added && added.indexOf(' ') > -1 ? added.split(' ')[1] : '';
        },
    },
    computed: {

        ...mapGetters({
            'user': "user/getUser",
        }),
    }
}
</script>

<style scoped>
.period_tabs {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
}

.period_tab_btn {
    border: 1px solid #eef0f5;
    background: #fff;
    border-radius: 999px;
    padding: 5px 14px;
    font-size: .8rem;
    color: #6b7280;
    transition: all .15s ease;
}

.period_tab_btn.active {
    background: #3b3f5c;
    border-color: #3b3f5c;
    color: #fff;
}

.stat_grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
}

@media (max-width: 767px) {
    .stat_grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

.stat_card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border: 1px solid #eef0f5;
    border-radius: 12px;
    background: #fff;
    transition: box-shadow .15s ease, transform .15s ease;
}

.stat_card:hover {
    box-shadow: 0 4px 14px rgba(20, 20, 40, .06);
    transform: translateY(-1px);
}

.stat_card__icon {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
}

.stat_card__body {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.stat_card__label {
    font-size: .75rem;
    opacity: .6;
    white-space: nowrap;
}

.stat_card__value {
    font-size: 1.4rem;
    font-weight: 600;
    line-height: 1.3;
}

.reviews_select_wrap {
    position: relative;
}

.reviews_select_icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    color: #9aa0ab;
    font-size: .85rem;
    pointer-events: none;
}

.reviews_select >>> .vs__dropdown-toggle {
    border: 1px solid #eef0f5;
    border-radius: 999px;
    padding: 4px 8px 4px 30px;
    background: #fff;
}

.reviews_select >>> .vs__selected-options {
    padding-left: 2px;
}

.reviews_select >>> .vs__search,
.reviews_select >>> .vs__search:focus {
    margin: 4px 0;
}

.reviews_select >>> .vs__dropdown-menu {
    border: 1px solid #eef0f5;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(20, 20, 40, .08);
    padding: 6px;
    max-height: 320px;
    margin-top: 4px;
}

.reviews_select >>> .vs__dropdown-option {
    border-radius: 8px;
    padding: 8px 10px;
    line-height: 1.3;
}

.reviews_select >>> .vs__dropdown-option--highlight {
    background: #eef1ff;
    color: #4f5bd5;
}

.rest_opt_title {
    font-size: .9rem;
}

.rest_opt_address {
    font-size: .75rem;
    opacity: .55;
}

.reviews_select >>> .vs__dropdown-option--highlight .rest_opt_address {
    opacity: .8;
}

.review_avatar {
    flex: 0 0 34px;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: #eef1ff;
    color: #4f5bd5;
    font-weight: 600;
    font-size: .85rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

.min-w-0 {
    min-width: 0;
}

.source_badge {
    background: #eef0f5;
    color: #475467;
    font-weight: 500;
    padding: .35em .8em;
    font-size: .78rem;
}

.source_opt_icon {
    display: inline-flex;
    width: 16px;
    height: 16px;
    margin-right: 6px;
    vertical-align: middle;
}

.source_opt_icon >>> svg {
    width: 100%;
    height: 100%;
}

.text-truncate-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.table thead th:first-child {
    max-width: 20px;
}

.reviews_table >>> thead th {
    border-top: none;
    border-bottom: 1px solid #eef0f5;
    font-weight: 500;
    font-size: .75rem;
    text-transform: uppercase;
    letter-spacing: .02em;
    opacity: .6;
    white-space: nowrap;
}

.reviews_table >>> tbody td {
    vertical-align: middle;
    padding: 14px 12px;
    border-top: none;
    border-bottom: 1px solid #f1f2f7;
}

.reviews_table >>> tbody tr:last-child td {
    border-bottom: none;
}

.reviews_table >>> tbody tr:hover {
    background-color: #fbfbfd;
}

.reviews_table >>> thead th .badge-info {
    background: #eef1ff;
    color: #4f5bd5;
    padding: .3em .4em;
}
</style>
